// src/components/DevStream/DevStreamEventCard.js
import React, { useState } from "react";
import "./DevStream.css";
import EventCardHeader from "./EventCardHeader";
import EventCardSummary from "./EventCardSummary";
import EventCardDetails from "./EventCardDetails";
import logger from "../../utils/logger";
import {
  getParsedMessage,
  extractSource,
  extractEventType,
  extractDeviceId,
  extractActionId,
  extractInstallationId,
  extractActionStatus,
  extractTraitName,
  extractWebhookHeaders,
  getRawEventBody,
  extractSubscriptionInstallationId,
  isOrphanedEvent,
} from "./utils/eventParsers";

/**
 * DevStreamEventCard - Individual event card with expand/collapse
 *
 * Features:
 * - Bordered card with key fields visible
 * - Full JSON collapsed by default
 * - Click to expand/collapse
 * - Copy button for individual event
 * - Source badge (YP, MC, or SYSTEM_ERROR)
 * - Timestamp display
 */
const DevStreamEventCard = ({ event }) => {
  const [expanded, setExpanded] = useState(false);

  const { timestamp, data } = event;

  // Parse event data using utility functions
  const parsedMessage = getParsedMessage(data);
  const source = extractSource(data);
  const eventType = extractEventType(data, parsedMessage);
  const deviceId = extractDeviceId(data, parsedMessage);
  const actionId = extractActionId(data, parsedMessage);
  const installationId = extractInstallationId(data, parsedMessage);
  const actionStatus = extractActionStatus(data, parsedMessage);
  const traitName = extractTraitName(data, parsedMessage);
  const webhookHeaders = extractWebhookHeaders(data, source);
  const rawEventBody = getRawEventBody(data, source);
  const subscriptionInstallationId = extractSubscriptionInstallationId(
    data,
    eventType
  );
  const isOrphaned = isOrphanedEvent(data, source);

  // Copy handlers
  const handleCopy = (e) => {
    e.stopPropagation();
    const combined = {
      ...(webhookHeaders && { headers: webhookHeaders }),
      body: rawEventBody,
    };
    copyToClipboard(e, combined, ".copy-event-btn");
  };

  const handleCopySection = (e, data, buttonClass) => {
    e.stopPropagation();
    copyToClipboard(e, data, `.${buttonClass}`);
  };

  const copyToClipboard = (e, data, buttonSelector) => {
    const text = JSON.stringify(data, null, 2);
    navigator.clipboard
      .writeText(text)
      .then(() => {
        const btn = e.target.closest(buttonSelector);
        if (btn) {
          const originalText = btn.textContent;
          btn.textContent = "✓";
          setTimeout(() => {
            btn.textContent = originalText;
          }, 2000);
        }
      })
      .catch((err) => {
        logger.error("Failed to copy:", err);
      });
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Build metadata object for expanded view
  const eventMetadata = {
    streamEventId: event.id,
    streamTimestamp: event.timestamp,
    source: source,
    organizationId: data?._devStreamMetadata?.organizationId,
    receivedAt: data?._devStreamMetadata?.receivedAt,
    ...(source === "MC" && {
      mcWebhookHeaders: data?.headers,
      mcOperation: data?.operation,
    }),
  };

  // Check for special styling hints
  const styleHint = data?._devStreamMetadata?.styleHint;
  const specialClass =
    styleHint === "subscription-confirmation"
      ? "subscription-confirmation"
      : "";

  return (
    <div
      className={`dev-stream-event-card ${
        expanded ? "expanded" : ""
      } ${specialClass}`}
      data-event={JSON.stringify(event)}
      data-source={source}
      data-orphaned={isOrphaned ? "true" : "false"}
    >
      <EventCardHeader
        source={source}
        eventType={eventType}
        timestamp={timestamp}
        expanded={expanded}
        onToggle={toggleExpanded}
        onCopy={handleCopy}
      />

      <EventCardSummary
        deviceId={deviceId}
        subscriptionInstallationId={subscriptionInstallationId}
        installationId={installationId}
        actionId={actionId}
        actionStatus={actionStatus}
        traitName={traitName}
        credentialId={data?.payload?.credentialId}
      />

      {expanded && (
        <EventCardDetails
          webhookHeaders={webhookHeaders}
          rawEventBody={rawEventBody}
          eventMetadata={eventMetadata}
          onCopySection={handleCopySection}
        />
      )}
    </div>
  );
};

export default DevStreamEventCard;
